import { Component } from '@theme/component';
import { createFrameTask, debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const HOVER_COMMIT_DELAY_MS = 150;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuResizeObserver = null;

  #submenuResizeFrame = null;

  #openMeasurementFrame;

  #submenuResizeGeneration = 0;

  #pointerReconcileFrame;

  #connectedHeader = null;

  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#connectedHeader = this.headerComponent;

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#resizeListener.cancel();
    this.removeEventListener('keydown', this.#onKeydown);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);

    if (this.#state.activeItem) {
      this.#deactivate(this.#state.activeItem, { force: true });
    } else {
      document.body.removeEventListener('pointermove', this.#onPointerMove);
      this.#cancelOpenMeasurement();
      this.#stopSubmenuSizeObservation();
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
    }

    this.#connectedHeader?.style.setProperty('--submenu-height', '0px');
    this.#connectedHeader?.style.setProperty('--full-open-header-height', '0px');
    this.#connectedHeader = null;
  }

  #resizeListener = debounce(() => {
    if (window.innerWidth < 990 && this.#state.activeItem) {
      this.#deactivate(this.#state.activeItem, { force: true });
    }
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;

    const control = this.#focusTargetAfterClose(item);
    this.#deactivate(item, { force: true });
    control.focus();
  };

  toggle = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const item = findMenuItem(button);
    if (!item) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {

      this.#deactivate(this.#state.activeItem ?? item, { force: true });

      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== button) focusTarget.focus();
    } else {
      this.activate(event);
    }
  };

  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }
    return this.#expandableFor(item);
  }

  #expandableFor(item) {
    const listItem = item.closest('.menu-list__list-item');
    const disclosure = listItem?.querySelector('[ref="disclosure[]"]');
    return disclosure instanceof HTMLElement ? disclosure : item;
  }

  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  #pointerIdleTimer;

  #lastPointer = { x: 0, y: 0 };

  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || Math.abs(event.movementY) >= 1;
    const safetyBox = `${moving}`;

    if (activeLink.dataset.safetyBox !== safetyBox) {
      activeLink.dataset.safetyBox = safetyBox;
    }

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    if (this.#pointerReconcileFrame !== undefined) {
      cancelAnimationFrame(this.#pointerReconcileFrame);
    }
    this.#pointerReconcileFrame = requestAnimationFrame(() => {
      this.#pointerReconcileFrame = undefined;
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    item.dataset.safetyBox = 'false';
  }

  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    if (this.#pointerReconcileFrame !== undefined) {
      cancelAnimationFrame(this.#pointerReconcileFrame);
      this.#pointerReconcileFrame = undefined;
    }
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  get overflowMenu() {
    return   (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return   (this.closest('header-component'));
  }

  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';
    const itemSubmenu = findSubmenu(item);

    if (isDefaultSlot && !itemSubmenu) {
      this.#deactivate(this.#state.activeItem, { force: true });
      return;
    }

    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;
    const nestedSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : itemSubmenu;
    const submenu = isDefaultSlot ? nestedSubmenu : this.overflowMenu;
    const hasSubmenu = Boolean(nestedSubmenu);

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;
    const moreTrigger = this.querySelector('[slot="more"] [ref="menuitem"]');

    if (previouslyActiveItem) {
      const previousControl = this.#expandableFor(previouslyActiveItem);
      if (previousControl !== moreTrigger && previousControl.hasAttribute('aria-expanded')) {
        previousControl.ariaExpanded = 'false';
      }
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
        previousSubmenu.inert = true;
      }
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      const previousOverflowControl = this.#expandableFor(previouslyActiveOverflowItem);
      if (previousOverflowControl.hasAttribute('aria-expanded')) {
        previousOverflowControl.ariaExpanded = 'false';
      }
      const previousOverflowSubmenu = findSubmenu(previouslyActiveOverflowItem);
      if (previousOverflowSubmenu) {
        delete previousOverflowSubmenu.dataset.active;
        previousOverflowSubmenu.inert = true;
      }
    }

    if (isDefaultSlot && this.overflowMenu) {
      delete this.overflowMenu.dataset.active;
      this.overflowMenu.inert = true;
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';

    const activeControl = this.#expandableFor(item);
    if (activeControl.hasAttribute('aria-expanded')) {
      activeControl.ariaExpanded = 'true';
    }
    if (moreTrigger instanceof HTMLElement) {
      moreTrigger.ariaExpanded = (!isDefaultSlot).toString();
    }
    if (overflowItem && overflowItem !== item) {
      const overflowControl = this.#expandableFor(overflowItem);
      if (overflowControl.hasAttribute('aria-expanded')) {
        overflowControl.ariaExpanded = 'true';
      }
    }

    if (nestedSubmenu) {
      nestedSubmenu.dataset.active = '';
      nestedSubmenu.inert = false;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      submenu.dataset.active = '';
      submenu.inert = false;

      this.#observeSubmenuSize({ item, submenu, hasSubmenu, isDefaultSlot }, nestedSubmenu);
    }

    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
    this.#scheduleOpenMeasurement({ item, submenu, hasSubmenu, isDefaultSlot });
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);

    const isMovingWithinItem =
      event.relatedTarget instanceof Node &&
      event.target instanceof Element &&
      event.target.contains(event.relatedTarget);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinItem || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;

    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#cancelOpenMeasurement();
    this.#stopSubmenuSizeObservation();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const itemSubmenu = findSubmenu(item);
    const isOverflowItem = Boolean(item.closest('[slot="more"], [slot="overflow"]'));
    const submenu = isOverflowItem ? this.overflowMenu : itemSubmenu;
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    const itemControl = this.#expandableFor(item);
    if (itemControl.hasAttribute('aria-expanded')) {
      itemControl.ariaExpanded = 'false';
    }
    const moreTrigger = this.querySelector('[slot="more"] [ref="menuitem"]');
    if (moreTrigger instanceof HTMLElement) {
      moreTrigger.ariaExpanded = 'false';
    }
    if (activeOverflowItem && activeOverflowItem !== item) {
      const overflowControl = this.#expandableFor(activeOverflowItem);
      if (overflowControl.hasAttribute('aria-expanded')) {
        overflowControl.ariaExpanded = 'false';
      }
      const activeOverflowSubmenu = findSubmenu(activeOverflowItem);
      if (activeOverflowSubmenu) {
        delete activeOverflowSubmenu.dataset.active;
        activeOverflowSubmenu.inert = true;
      }
    }

    if (itemSubmenu && itemSubmenu !== submenu) {
      delete itemSubmenu.dataset.active;
      itemSubmenu.inert = true;
    }
    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuItems = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuItems?.length || !this.overflowMenu) return this.overflowMenu?.offsetHeight || 0;

    const rowRects = slottedMenuItems
      .map(
        (item) =>
          item.querySelector(
            ':scope > .menu-list__link-wrapper, :scope > .menu-list__trigger, :scope > .menu-list__link',
          ) ?? item,
      )
      .filter((item) => item instanceof HTMLElement && item.getClientRects().length > 0)
      .map((item) => item.getBoundingClientRect());

    if (rowRects.length === 0) return this.overflowMenu.offsetHeight;

    const styles = getComputedStyle(this.overflowMenu);
    const paddingBlockStart = Number.parseFloat(styles.paddingBlockStart) || 0;
    const paddingBlockEnd = Number.parseFloat(styles.paddingBlockEnd) || 0;
    const top = Math.min(...rowRects.map((rect) => rect.top));
    const bottom = Math.max(...rowRects.map((rect) => rect.bottom));

    return Math.ceil(bottom - top + paddingBlockStart + paddingBlockEnd);
  }

  #scheduleOpenMeasurement(measurement) {
    this.#cancelOpenMeasurement();
    this.#openMeasurementFrame = requestAnimationFrame(() => {
      this.#openMeasurementFrame = undefined;
      this.#applyOpenMeasurement(measurement);
    });
  }

  #applyOpenMeasurement({ item, submenu, hasSubmenu, isDefaultSlot }) {
    if (this.#state.activeItem !== item || !this.headerComponent) return 0;

    let finalHeight = submenu?.offsetHeight || 0;
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      finalHeight = hasSubmenu ? Math.max(this.overflowMenu?.offsetHeight || 0, overflowListHeight) : overflowListHeight;
    }
    if (!submenu) finalHeight = 0;

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    const itemRect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent.querySelector('.header__row--top') : this.headerComponent;
    const safetyBoxHeight = Math.max(0, (boundary?.getBoundingClientRect().bottom ?? 0) - itemRect.top);

    const submenuHeight = `${finalHeight}px`;
    const boxHeight = `${safetyBoxHeight}px`;
    if (this.headerComponent.style.getPropertyValue('--submenu-height') !== submenuHeight) {
      this.headerComponent.style.setProperty('--submenu-height', submenuHeight);
    }
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    if (item.style.getPropertyValue('--box-height') !== boxHeight) {
      item.style.setProperty('--box-height', boxHeight);
    }

    return finalHeight;
  }

  #cancelOpenMeasurement() {
    if (this.#openMeasurementFrame === undefined) return;
    cancelAnimationFrame(this.#openMeasurementFrame);
    this.#openMeasurementFrame = undefined;
  }

  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ?   (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    const height = `${fullOpenHeaderHeight}px`;
    if (this.headerComponent.style.getPropertyValue('--full-open-header-height') !== height) {
      this.headerComponent.style.setProperty('--full-open-header-height', height);
    }
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #observeSubmenuSize(measurement, nestedSubmenu) {
    this.#stopSubmenuSizeObservation();
    const generation = this.#submenuResizeGeneration;
    const frame = createFrameTask(() => {
      if (!this.isConnected || generation !== this.#submenuResizeGeneration) return;
      if (this.#state.activeItem !== measurement.item || !measurement.submenu?.isConnected) return;
      this.#applyOpenMeasurement(measurement);
    });
    this.#submenuResizeFrame = frame;
    this.#submenuResizeObserver = new ResizeObserver(() => frame.schedule());

    const targets = new Set([
      measurement.submenu,
      nestedSubmenu,
      nestedSubmenu?.querySelector('.menu-list__submenu-inner'),
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]'),
    ]);
    for (const target of targets) {
      if (target instanceof Element) this.#submenuResizeObserver.observe(target);
    }
    frame.schedule();
  }

  #stopSubmenuSizeObservation() {
    this.#submenuResizeGeneration += 1;
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
    this.#submenuResizeFrame?.cancel();
    this.#submenuResizeFrame = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item') ?? element;
  return listItem.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
